/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './ColorIconPicker.scss';

import { IconButton } from '@fluentui/react/lib/Button';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Label } from '@fluentui/react/lib/Label';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { getAppHost } from '../../../utilities/TeamsUtils';
import { getColorButtonStyles, getIconButtonStyles } from './ColorIconPicker.styles';
import { fontIcons, listColorCollection } from './MediaList';

export interface ColorIconPickerProps {
    selectedColor?: string;
    selectedIcon?: string;
    onColorSelected?: (color: string) => void;
    onIconSelected?: (iconName: string) => void;
}

export const ColorIconPicker = (props: ColorIconPickerProps): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const { selectedColor, selectedIcon, onColorSelected, onIconSelected } = props;

    return (
        <>
            <div className='colorIconPicker-section'>
                <div>
                    <Label>{loc.strings.choose_color_label}</Label>
                </div>
                <FocusZone direction={FocusZoneDirection.horizontal}>
                    <div role='listbox' className='colorIconPicker-pickerContainer'>
                        {listColorCollection.map(
                            (
                                colorInfo: {
                                    id: string;
                                    color: string;
                                },
                                index: number
                            ): JSX.Element => {
                                return (
                                    <IconButton
                                        key={index}
                                        role='option'
                                        ariaLabel={colorInfo.color}
                                        styles={getColorButtonStyles(theme, colorInfo.color)}
                                        className='colorIconPicker-tile colorTile'
                                        checked={colorInfo.color === selectedColor}
                                        onClick={(): void => {
                                            if (onColorSelected) {
                                                onColorSelected(colorInfo.color);
                                            }
                                        }}
                                    />
                                );
                            }
                        )}
                    </div>
                </FocusZone>
            </div>
            <div className='colorIconPicker-section'>
                <div>
                    <Label>{loc.strings.choose_icon_label}</Label>
                </div>
                <FocusZone direction={FocusZoneDirection.horizontal}>
                    <div role='listbox' className='colorIconPicker-pickerContainer'>
                        {fontIcons.map((fontIcon: { name: string }, index: number): JSX.Element => {
                            return (
                                <IconButton
                                    key={index}
                                    role='option'
                                    ariaLabel={fontIcon.name}
                                    styles={getIconButtonStyles(theme, selectedColor)}
                                    iconProps={{ iconName: fontIcon.name }}
                                    className={`colorIconPicker-tile iconTile ${
                                        fontIcon.name === selectedIcon && 'color-selected'
                                    }`}
                                    checked={fontIcon.name === selectedIcon}
                                    onClick={(): void => {
                                        if (onIconSelected) {
                                            onIconSelected(fontIcon.name);
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                </FocusZone>
            </div>
        </>
    );
};
